<template>
    <v-container class="pa-5" fluid>
        <page-title>Lista zamówień</page-title>

        <v-row align="center">
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row justify="end" align="center" dense>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="filters.status_id"
                                label="Ukryj wysłane"
                                hide-details
                                class="mt-0"
                                true-value="10"
                                false-value="10,50"
                                @change="changeFilter"
                            />
                        </v-col>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="filters.showWithCooperantNumber"
                                label="Z numerem kooperanta"
                                hide-details
                                class="mt-0"
                                @change="getOrders"
                            />
                        </v-col>
                        <v-col cols="12" md="auto" sm="auto">
                            <v-menu
                                v-model="plannedSendDateMenuFrom"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template #activator="{on}">
                                    <v-text-field
                                        v-model="filters.plannedSendDate__gte"
                                        label="Data wysyłki od"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        clearable
                                        solo
                                        hide-details
                                        v-on="on"
                                        @input="changeFilter"
                                        @click:clear="clearFilter('plannedSendDate__gte')"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="filters.plannedSendDate__gte"
                                    first-day-of-week="1"
                                    @change="changeFilter"
                                    @input="plannedSendDateMenuFrom = false"
                                />
                            </v-menu>
                        </v-col>
                        <v-col cols="12" md="auto" sm="auto">
                            <v-menu
                                v-model="plannedSendDateMenuTo"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                            >
                                <template #activator="{on}">
                                    <v-text-field
                                        v-model="filters.plannedSendDate__lte"
                                        label="Data wysyłki do"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        clearable
                                        solo
                                        hide-details
                                        v-on="on"
                                        @input="changeFilter"
                                        @click:clear="clearFilter('plannedSendDate__lte')"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="filters.plannedSendDate__lte"
                                    first-day-of-week="1"
                                    @change="changeFilter"
                                    @input="plannedSendDateMenuTo = false"
                                />
                            </v-menu>
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-text-field
                                v-model="filters.search"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row justify="end" no-gutters>
            <v-col cols="auto">
                <v-btn
                    text
                    x-small
                    data-sentry-label="Generuj raport"
                    :disabled="actionLoading.generateReport"
                    @click="generateCooperantOrdersReport"
                >
                    <v-icon
                        v-if="!actionLoading.generateReport"
                        small
                        color="green"
                    >
                        mdi-file-excel
                    </v-icon>
                    <v-progress-circular
                        v-else
                        indeterminate
                        color="primary"
                        class="mr-1"
                        size="12"
                        width="2"
                    />
                    Generuj raport
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="orders"
                        :loading="ordersLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="ordersData"
                        @pagination="updatePagination"
                    >
                        <template #item.cooperantNumber="{item}">
                            {{ cooperantNumber(item) }}
                        </template>
                        <template #item.isCOD="{item}">
                            <v-icon
                                v-if="item.isCOD"
                                small
                            >
                                mdi-account-cash
                            </v-icon>
                            <v-icon
                                v-else
                                small
                                color="error"
                            >
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.quantity="{item}">
                            {{ `${item.quantity} szt.` }}

                            <v-tooltip v-if="item.patterns.length > 1" :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-icon color="info" v-on="on">
                                        mdi-plus-circle
                                    </v-icon>
                                </template>
                                <span style="white-space: pre-line">{{ sortedPatterns(item) }}</span>
                            </v-tooltip>
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.plannedSendDate="{item}">
                            {{ formatDate(item.plannedSendDate, false) }}
                        </template>
                        <template #item.shipmentStatus="{item}">
                            {{ item.shipmentStatus.name }}

                            <v-tooltip v-if="item.overdue" :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-icon color="warning" small v-on="on">
                                        mdi-clock-outline
                                    </v-icon>
                                </template>
                                <span>Opóźnione</span>
                            </v-tooltip>
                        </template>
                        <template #item.actions="{item}">
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-btn
                                        icon
                                        data-sentry-label="Otwórz"
                                        v-on="on"
                                        @click="openOrder(item.id)"
                                    >
                                        <v-icon color="blue">
                                            mdi-ballot
                                        </v-icon>
                                    </v-btn>
                                </template>
                                <span>Otwórz</span>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import PageTitle from '@/components/common/PageTitle'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'CooperantOrders',
    components: {
        PageTitle
    },
    mixins: [
        tooltipDelay,
        filters,
        pagination,
        dateFormatter,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('orders/clearCooperantOrdersFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            user: {},
            hasCooperantNumber: false,
            plannedSendDateMenuFrom: false,
            plannedSendDateMenuTo: false,
            actionLoading: {
                generateReport: false
            },
            headers: [
                {
                    text: 'Numer zamówienia',
                    value: 'shopOrderNumber',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Numer zamówienia kooperanta',
                    value: 'cooperantNumber',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Nazwa sklepu',
                    value: 'shopName',
                    sortable: false,
                    align: 'left'
                },
                {
                    text: 'Faktura',
                    value: 'invoiceData',
                    sortable: false,
                    align: 'left'
                },
                {
                    text: 'Nazwa produktu',
                    value: 'shopProductName',
                    sortable: false,
                    align: 'left'
                },
                {
                    text: 'Nakład',
                    value: 'quantity',
                    sortable: false,
                    align: 'center',
                    width: 110
                },
                {
                    text: 'Data zamówienia',
                    value: 'createdAt',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Planowana data wysyłki',
                    value: 'plannedSendDate',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'COD',
                    value: 'isCOD',
                    sortable: false,
                    align: 'left'
                },
                {
                    text: 'Status',
                    value: 'shipmentStatus',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        userId() {
            return this.$store.state.user.userId
        },
        orders() {
            return this.$store.state.orders.cooperantsOrder.orders
        },
        ordersLoading() {
            return this.$store.state.orders.cooperantsOrder.ordersLoading
        },
        savedFilters() {
            return this.$store.state.orders.cooperantsOrder.filters
        },
        selectedOrderId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.$store.dispatch('administration/getUser', this.userId )
            .then(response => {
                this.user = response.data
            })
            .catch(error => {
                errorHandler(error)
            })

        this.getOrders()
    },
    methods: {
        sortedPatterns(item) {
            return [...item.patterns]
                .sort((a, b) => a.id - b.id)
                .map(pattern => `Wzór ${pattern.number} - ${pattern.quantity} szt.`)
                .join('\n')
        },
        cooperantNumber(item) {
            return item.cooperantNumber ? item.cooperantNumber : '-'
        },
        setSelectedRow(item) {
            if (item.id === this.selectedOrderId) {
                return 'isSelected'
            }
        },
        getOrders() {
            this.$store.dispatch('orders/getCooperantOrders', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openOrder(orderId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(orderId)
            router.push({
                name: 'ordersCooperantOrdersOrder',
                params: {
                    id: orderId,
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        generateCooperantOrdersReport() {
            this.actionLoading.generateReport = true
            this.$store.dispatch('orders/generateCooperantOrdersListReport', {
                query: this.filterQuery,
                userEmail: this.user.email
            })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.generateReport = false
                })
        },
        changeFilter() {
            this.$store.dispatch('orders/setCooperantOrdersFilters', this.filters)
            this.resetPagination()
            this.getOrders()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getOrders()
            }
            this.clearPaginationPending()
        },
    }
}
</script>
